/**
 * 源站 orocasino.mx `#initial-splash`：HTML 首屏黑底 + logo2 + 红白进度条。
 * 仅 MX 构建注入；Vue mount / runtime boot 后再淡出拆除。
 */

export const ORO_MX_INITIAL_SPLASH_ID = 'initial-splash';
export const ORO_MX_INITIAL_SPLASH_LEAVE_MS = 450;

const LOGO_PATH = 'skins/oro-mx-h5/brand/life-logo.png';
const FAVICON_PATH = 'skins/oro-mx-h5/brand/life-icon.png';
const SHARE_IMAGE_PATH = 'skins/oro-mx-h5/share/recompensa-100c.jpg';
const SOURCE_TITLE = 'Casinolife';
const SHARE_TITLE = 'RECOMPENSA EXCLUSIVA';
const SHARE_DESCRIPTION = 'Recibe un bonos de $100. Reclámala ahora.';

export function isOroMxInitialSplashBuild(env: {
  VITE_SITE_ID?: string;
  VITE_SKIN_CODE?: string;
}) {
  return env.VITE_SKIN_CODE === 'oro-mx-h5' || env.VITE_SITE_ID === 'orocasino-mx-h5';
}

function normalizedBase(baseUrl: string) {
  const trimmed = (baseUrl || '/').trim() || '/';
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
}

function oroMxPublicUrl(baseUrl: string, relativePath: string) {
  return `${normalizedBase(baseUrl)}${relativePath}`;
}

function oroMxShareOrigin(baseUrl: string) {
  return normalizedBase(baseUrl) === '/mx/' ? 'https://h5.wjgame.top' : 'https://lifecasino.mx';
}

/** 主链接必须带 og:url，否则 Telegram 会把根地址记成空预览。 */
export function oroMxSharePageUrl(baseUrl: string) {
  const base = normalizedBase(baseUrl);
  return base === '/' ? `${oroMxShareOrigin(baseUrl)}/` : `${oroMxShareOrigin(baseUrl)}${base}`;
}

function oroMxLinkPreviewTags(imageUrl: string, pageUrl: string) {
  return [
    `<meta name="description" content="${SHARE_DESCRIPTION}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="${SOURCE_TITLE}" />`,
    `<meta property="og:url" content="${pageUrl}" />`,
    `<meta property="og:title" content="${SHARE_TITLE}" />`,
    `<meta property="og:description" content="${SHARE_DESCRIPTION}" />`,
    `<meta property="og:image" content="${imageUrl}" />`,
    `<meta property="og:image:type" content="image/jpeg" />`,
    `<meta property="og:image:width" content="1280" />`,
    `<meta property="og:image:height" content="720" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${SHARE_TITLE}" />`,
    `<meta name="twitter:description" content="${SHARE_DESCRIPTION}" />`,
    `<meta name="twitter:image" content="${imageUrl}" />`,
  ].join('');
}

export function oroMxInitialSplashLogoUrl(baseUrl: string) {
  return oroMxPublicUrl(baseUrl, LOGO_PATH);
}

/** 源站 `/favicon.ico` 对应 CDN 圆形品牌图，已镜像到皮肤目录。 */
export function oroMxFaviconUrl(baseUrl: string) {
  return oroMxPublicUrl(baseUrl, FAVICON_PATH);
}

/**
 * Telegram 只读 HTML，且 og:image 必须是绝对地址。
 * 正式线挂根路径，图走主站；测试线挂 /mx/，图走测试 H5。
 */
export function oroMxShareImageUrl(baseUrl: string) {
  return `${oroMxShareOrigin(baseUrl)}${oroMxPublicUrl(baseUrl, SHARE_IMAGE_PATH)}`;
}

export function injectOroMxInitialSplash(html: string, baseUrl: string) {
  if (html.includes(`id="${ORO_MX_INITIAL_SPLASH_ID}"`)) return html;

  const logoSrc = oroMxInitialSplashLogoUrl(baseUrl);
  const faviconSrc = oroMxFaviconUrl(baseUrl).replace(/"/g, '&quot;');
  const headBits = [oroMxFaviconLink(faviconSrc), oroMxInitialSplashStyleTag(), oroMxInitialSplashRemScript()].join(
    '',
  );
  const previewTags = oroMxLinkPreviewTags(oroMxShareImageUrl(baseUrl), oroMxSharePageUrl(baseUrl));
  const next = html
    .replace(/<head(\s[^>]*)?>/i, (open) => `${open}${previewTags}`)
    .replace(
      /(<meta\s+name="theme-color"\s+content=")#[0-9a-fA-F]+("\s*\/?>)/,
      '$1#000000$2',
    )
    .replace(/<title>[^<]*<\/title>/, `<title>${SOURCE_TITLE}</title>`)
    .replace('</head>', `${headBits}</head>`)
    .replace('</body>', `${oroMxInitialSplashMarkup(logoSrc)}</body>`);

  return next;
}

export function dismissOroMxInitialSplash(onRemoved?: () => void) {
  const el = document.getElementById(ORO_MX_INITIAL_SPLASH_ID);
  if (!el) {
    onRemoved?.();
    return;
  }

  el.classList.add('initial-splash--leave');
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    try {
      el.remove();
    } catch {
      /* already detached */
    }
    onRemoved?.();
  };

  el.addEventListener('transitionend', finish, { once: true });
  window.setTimeout(finish, ORO_MX_INITIAL_SPLASH_LEAVE_MS);
}

function oroMxFaviconLink(href: string) {
  return `<link rel="icon" type="image/png" href="${href}" />`;
}

function oroMxInitialSplashStyleTag() {
  return `<style id="initial-splash-styles">
    /* 首屏固定 px，不跟随 html 根字号 */
    #initial-splash {
      position: fixed;
      inset: 0;
      z-index: 2147483000;
      margin: 0;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #000000;
      font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    }

    #initial-splash.initial-splash--leave {
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.42s ease;
    }

    .initial-splash__inner {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.85333rem;
      width: 100%;
      max-width: 12rem;
      padding: 0 0.85333rem;
      box-sizing: border-box;
    }

    .initial-splash__logo {
      display: block;
      width: min(72vw, 8.53333rem);
      height: auto;
      -o-object-fit: contain;
         object-fit: contain;
      -webkit-user-select: none;
         -moz-user-select: none;
              user-select: none;
      -webkit-user-drag: none;
    }

    .initial-splash__progress {
      position: relative;
      width: min(68vw, 8rem);
      height: 0.08rem;
      border-radius: 26.64rem;
      overflow: hidden;
      background: rgba(255, 255, 255, 0.08);
    }

    .initial-splash__progress-bar {
      position: absolute;
      inset: 0 auto 0 0;
      width: 100%;
      height: 100%;
      border-radius: inherit;
      background: linear-gradient(90deg, #EE1E40 0%, #EE1E40 28%, #ffffff 100%);
      transform-origin: left center;
      animation: initial-splash-progress 1.8s ease-in-out infinite;
    }

    @keyframes initial-splash-progress {
      0% {
        transform: scaleX(0.12);
        opacity: 0.85;
      }

      45% {
        transform: scaleX(0.72);
        opacity: 1;
      }

      100% {
        transform: scaleX(1);
        opacity: 1;
      }
    }
  </style>`;
}

function oroMxInitialSplashRemScript() {
  return `<script>
    (function () {
      var D = 750, M = 450, B = 37.5;
      var w = Math.min(Math.max(document.documentElement.clientWidth || window.innerWidth || D, 1), M);
      document.documentElement.style.fontSize = (w / D * B) + "px";
    })();
  </script>`;
}

function oroMxInitialSplashMarkup(logoSrc: string) {
  const src = logoSrc.replace(/"/g, '&quot;');
  return `<div id="initial-splash" aria-busy="true" aria-live="polite" aria-label="Loading">
    <div class="initial-splash__inner">
      <img src="${src}" alt="logo" class="initial-splash__logo" />
      <div class="initial-splash__progress" aria-hidden="true">
        <div class="initial-splash__progress-bar"></div>
      </div>
    </div>
  </div>`;
}
